import React, { useState, useEffect } from 'react';
import { Scan, Activity } from 'lucide-react';

const CARDS = [
    {
        id: 1,
        name: "Charizard VMAX",
        rarity: "Secret Rare",
        value: "$1,240.50",
        trend: "+24%",
        image: "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?q=80&w=800&auto=format&fit=crop",
        color: "from-orange-500 to-red-600",
        hue: "0deg"
    },
    {
        id: 2,
        name: "Blastoise GX",
        rarity: "Ultra Rare",
        value: "$890.00",
        trend: "+12%",
        image: "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?q=80&w=800&auto=format&fit=crop",
        color: "from-blue-500 to-cyan-600",
        hue: "200deg" // Simulated Blue
    },
    {
        id: 3,
        name: "Venusaur EX",
        rarity: "Holofoil",
        value: "$650.00",
        trend: "+8%",
        image: "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?q=80&w=800&auto=format&fit=crop",
        color: "from-emerald-500 to-green-600",
        hue: "100deg" // Simulated Green
    }
];

const HeroCarousel = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % CARDS.length);
        }, 4000); // Rotate every 4 seconds
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="relative w-[340px] h-[480px] perspective-[2000px] group">
            {CARDS.map((card, index) => {
                // Calculate position relative to active index
                let position = 0; // 0 = active, 1 = next, -1 = prev
                if (index === activeIndex) position = 0;
                else if (index === (activeIndex + 1) % CARDS.length) position = 1;
                else position = -1;

                // Visibility & Transform Logic
                const isActive = position === 0;
                const isNext = position === 1;
                const isPrev = position === -1;

                // Hide cards that aren't current, next, or prev for cleanDOM
                if (!isActive && !isNext && !isPrev) return null;

                return (
                    <div
                        key={card.id}
                        className={`absolute inset-0 transition-all duration-700 ease-out preserve-3d
              ${isActive ? 'z-20 opacity-100 scale-100 translate-x-0' : ''}
              ${isNext ? 'z-10 opacity-40 scale-90 translate-x-[40%] translate-z-[-100px] rotate-y-[-15deg]' : ''}
              ${isPrev ? 'z-10 opacity-40 scale-90 -translate-x-[40%] translate-z-[-100px] rotate-y-[15deg]' : ''}
            `}
                    >
                        {/* CARD CONTAINER */}
                        <div className={`relative w-full h-full bg-slate-900 rounded-[20px] border border-slate-700 shadow-2xl overflow-hidden
              ${isActive ? 'group-hover:rotate-y-0 group-hover:rotate-x-0 transform transition-transform duration-500 rotate-y-12 rotate-x-6' : ''}
            `}>

                            {/* Card Face */}
                            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950 p-2">
                                <div
                                    className="w-full h-full bg-cover bg-center rounded-xl relative overflow-hidden"
                                    style={{
                                        backgroundImage: `url(${card.image})`,
                                        filter: `hue-rotate(${card.hue})`
                                    }}
                                >
                                    {/* Tech Overlay */}
                                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] opacity-30" />

                                    {/* Floating Stats (Only on Active) */}
                                    {isActive && (
                                        <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-md border border-white/10 p-3 rounded-lg animate-in fade-in slide-in-from-bottom-2 duration-500">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs font-bold text-slate-300 uppercase">Market Value</span>
                                                <span className={`font-mono font-bold bg-clip-text text-transparent bg-gradient-to-r ${card.color}`}>
                                                    {card.value}
                                                </span>
                                            </div>
                                            <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                                                <div className={`h-full bg-gradient-to-r ${card.color} w-[75%] animate-pulse`} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Glow Behind (Active Only) */}
                            {isActive && (
                                <div className={`absolute -inset-1 bg-gradient-to-r ${card.color} rounded-[24px] opacity-20 blur-xl -z-10 group-hover:opacity-40 transition-opacity`} />
                            )}
                        </div>

                        {/* Context Floating Elements (Only Active) */}
                        {isActive && (
                            <>
                                <div className="absolute top-20 -right-12 bg-slate-900/90 backdrop-blur-md border border-slate-700 p-4 rounded-xl shadow-xl animate-float-slow delay-100 z-30">
                                    <Activity className="w-6 h-6 text-green-500 mb-2" />
                                    <div className="text-xs text-slate-400 uppercase font-bold">Trend</div>
                                    <div className="text-xl font-bold text-white">{card.trend}</div>
                                </div>

                                <div className="absolute bottom-32 -left-12 bg-slate-900/90 backdrop-blur-md border border-slate-700 p-4 rounded-xl shadow-xl animate-float-slow delay-500 z-30">
                                    <Scan className={`w-6 h-6 ${index === 0 ? 'text-orange-500' : index === 1 ? 'text-blue-500' : 'text-emerald-500'} mb-2`} />
                                    <div className="text-xs text-slate-400 uppercase font-bold">Rarity</div>
                                    <div className="text-xl font-bold text-white">{card.rarity}</div>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default HeroCarousel;
